import React from 'react';

import EastIcon from '@mui/icons-material/East';

import DashedEastIcon from 'components/Icons/DashedEastIcon';

import { getBandColor, getRiskBand } from './bands';
import { LIKELIHOOD_LEVELS, SEVERITY_LEVELS } from './constants';
import RiskMatrixGrid from './RiskMatrixGrid';
import {
  BandName,
  DashedArrowColor,
  MatrixColumnTitle,
  NoChangeLabel,
  PanelLayout,
  PanelScroller,
  ScoreBadge,
  ScoreDelta,
  ScoreSummaryRow,
  ScoreTransition,
  UnratedBadge,
} from './styles';
import { isRated, RiskMatrixValue, RiskScoringPanelProps } from './types';

const CELL_SIZE = 32;

const ScoreSummary = ({ value, testId }: { value?: RiskMatrixValue | null; testId: string }): React.ReactElement => {
  if (!isRated(value)) {
    return (
      <ScoreSummaryRow data-testid={testId}>
        <UnratedBadge>Not rated</UnratedBadge>
      </ScoreSummaryRow>
    );
  }

  const band = getRiskBand(value.severity, value.likelihood, SEVERITY_LEVELS, LIKELIHOOD_LEVELS);
  const { bg, color } = getBandColor(band);

  return (
    <ScoreSummaryRow data-testid={testId}>
      <ScoreBadge $bg={bg} $color={color} aria-label={`${band}, score ${value.score}`}>
        {value.score}
      </ScoreBadge>
      <BandName>{band}</BandName>
    </ScoreSummaryRow>
  );
};

const ScoreTransitionCell = ({
  from,
  to,
  isTarget = false,
}: {
  from?: RiskMatrixValue | null;
  to?: RiskMatrixValue | null;
  isTarget?: boolean;
}): React.ReactElement => {
  const reduction = isRated(from) && isRated(to) ? from.score - to.score : undefined;

  return (
    <ScoreTransition>
      {isTarget ? (
        <DashedArrowColor>
          <DashedEastIcon />
        </DashedArrowColor>
      ) : (
        <EastIcon fontSize="small" />
      )}
      {reduction !== undefined &&
        (reduction === 0 ? (
          <NoChangeLabel>No change</NoChangeLabel>
        ) : (
          <ScoreDelta $improved={reduction > 0}>{reduction > 0 ? `−${reduction}` : `+${-reduction}`}</ScoreDelta>
        ))}
    </ScoreTransition>
  );
};

const RiskScoringPanel = ({
  inherent,
  residual,
  target,
  onChangeInherent,
  onChangeResidual,
  onChangeTarget,
}: RiskScoringPanelProps): React.ReactElement => {
  const select = (handler?: (value: RiskMatrixValue | null) => void): ((l: number, s: number) => void) | undefined =>
    handler && ((likelihood, severity): void => handler({ likelihood, severity, score: likelihood * severity }));

  const clear = (handler?: (value: RiskMatrixValue | null) => void): (() => void) | undefined =>
    handler && ((): void => handler(null));

  return (
    <PanelScroller>
      <PanelLayout data-testid="risk-scoring-panel" $columns={5}>
        <MatrixColumnTitle>Inherent</MatrixColumnTitle>
        <span />
        <MatrixColumnTitle>Residual</MatrixColumnTitle>
        <span />
        <MatrixColumnTitle>Target</MatrixColumnTitle>

        <RiskMatrixGrid
          value={inherent}
          cellSize={CELL_SIZE}
          onSelect={select(onChangeInherent)}
          onClear={clear(onChangeInherent)}
          testId="risk-matrix-inherent"
        />
        <ScoreTransitionCell from={inherent} to={residual} />
        <RiskMatrixGrid
          value={residual}
          cellSize={CELL_SIZE}
          onSelect={select(onChangeResidual)}
          onClear={clear(onChangeResidual)}
          testId="risk-matrix-residual"
        />
        <ScoreTransitionCell from={residual} to={target} isTarget />
        <RiskMatrixGrid
          value={target}
          cellSize={CELL_SIZE}
          onSelect={select(onChangeTarget)}
          onClear={clear(onChangeTarget)}
          testId="risk-matrix-target"
        />

        <ScoreSummary value={inherent} testId="risk-score-inherent" />
        <span />
        <ScoreSummary value={residual} testId="risk-score-residual" />
        <span />
        <ScoreSummary value={target} testId="risk-score-target" />
      </PanelLayout>
    </PanelScroller>
  );
};

export default RiskScoringPanel;
